import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { updateFormData, prevStep, resetForm } from "../../redux/formSlice";
import { Alert, Box, Divider, Typography } from "@mui/material";
import { OkSanad } from "../../services/sanad/sanad";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import QRCode from "react-qr-code";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import i18n from "../../i18n";
import StepNavigation from "../../components/StepNavigation";
import { trackEvent, trackFormSubmit } from "../../utils/analytics";

function Confirm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { handleSubmit } = useForm();
  const formData = useSelector((state) => state.form.formData);
  const [selectedName] = useState(formData.name); // Track selected name
  const [selectedTime] = useState(formData.time); // Track selected time
  const [loading, setLoading] = useState(false); // Track loading state
  const [progress, setProgress] = useState(0);
  const { t } = useTranslation();
  const onSubmit = (data) => {
    dispatch(updateFormData(data));
    handleSanadConfirm(); // Call directly to show result on submit
  };
  const MySwal = withReactContent(Swal);
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 120) {
          return 120;
        }

        return oldProgress + 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);
  const QRCodeModal = ({ codesanad, codepoya }) => (
    <>
      <div className="flex flex-col items-center gap-3 justify-center">
        <div className=" flex flex-col items-center gap-3 justify-center">
          <p className="text-lg">
            {t("QR.Code")}{" "}
            <span className="text-xl font-semibold">{codesanad}</span>
          </p>
          <p className="text-lg">
            {t("QR.Otp")}{" "}
            <span className="text-xl font-semibold">{codepoya}</span>
          </p>
        </div>
        <div className="border-purple border-2 rounded-lg p-3">
          <QRCode value={`${codesanad}-${codepoya}`} size={200} />
        </div>
        <Alert
          severity="info"
          sx={{ fontFamily: "vazirmatn" }}
          slotProps={{
            icon: {
              sx: {
                ml: i18n.dir() === "rtl" ? 1.5 : 0, // dynamic RTL support
                mr: i18n.dir() === "rtl" ? 0 : 1.5,
              },
            },
          }}
        >
          {t("QR.Note")}
        </Alert>
      </div>
    </>
  );

  const handleSanadConfirm = async () => {
    setLoading(true); // Trigger loading when the confirm button is clicked
    
    // Track upload initiation
    trackEvent("File Upload", "Initiate", formData.name || "Unknown");
    trackFormSubmit("File Upload");
    
    const data = {
      namesanad: selectedName,
      timesave: selectedTime,
    };

    try {
      const response = await OkSanad(data);
      const { codesanad, codepoya } = response.data;

      // Check if codesanad equals 1000000 and throw an error if it does
      if (codesanad === 1000000) {
        throw new Error(t("Upload.InvalidCode"));
      }

      // Track successful upload
      trackEvent(
        "File Upload",
        "Success",
        formData.name || "Unknown",
        parseInt(formData.page) || 0,
        {
          fileName: formData.name,
          pageCount: formData.page,
          fileSize: formData.size,
          printStatus: formData.status === "1" ? "One Side" : "Two Side",
          colorType: formData.type === "1" ? "BW" : "Color",
          accessTime: formData.time,
        }
      );

      await MySwal.fire({
        title: t("Progress.success"),
        icon: "success",
        timer: 1500, // 2 seconds timer
        showConfirmButton: false,
      });

      // Show the second modal with codesanad, codepoya, and QR code
      await MySwal.fire({
        html: <QRCodeModal codesanad={codesanad} codepoya={codepoya} />, // Use the React component
        confirmButtonColor: "#512C63",
        // confirmButtonText: "تایید",
        timer: 120000, // 10 seconds timer
        confirmButtonText: t("Progress.ok"),
        customClass: {
          title: "swal2-title-custom",
        },
      });
      navigate("/"); // Navigate only after confirmation

      dispatch(resetForm());
    } catch (err) {
      // Track failed upload
      const errorMessage =
        err.response?.data?.message || err.message || t("Upload.Err");
      trackEvent(
        "File Upload",
        "Error",
        formData.name || "Unknown",
        0,
        {
          error: errorMessage,
          fileName: formData.name,
        }
      );
      
      Swal.fire({
        title: t("Upload.Err"),
        text: errorMessage,
        confirmButtonText: t("Progress.ok"),
        customClass: {
          title: "swal2-title-custom",
        },
      });

      toast.error(t("Upload.Err"), { id: "submission-toast" }); // Show error toast
      dispatch(resetForm()); // Reset form even if there is an error
    } finally {
      setLoading(false); // Stop loading after the submission process is complete
      toast.dismiss("submission-toast"); // Remove the loading toast
    }
  };

  return (
    <>
      <Box>
        <Typography
          variant="h3"
          sx={{ textAlign: "center", marginBottom: ".5rem" }}
        >
          {t("Upload.Sure")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            padding: "1rem",
            gap: ".5rem",
            marginBottom: "1rem",
          }}
          className="bg-[#CAC4D0] rounded-2xl mb-3"
        >
          <div className="flex justify-between w-full items-center">
            <Typography variant="h5">{t("Upload.Filename")}</Typography>
            <Typography variant="h4">{formData.name}</Typography>
          </div>
          <Divider />
          <div className="flex justify-between w-full items-center">
            <Typography variant="h5">{t("Upload.Pagenum")}</Typography>
            <Typography variant="h4">{formData.page}</Typography>
          </div>
          <Divider />
          <div className="flex justify-between w-full items-center">
            <Typography variant="h5">{t("Upload.Size")}</Typography>
            <Typography variant="h4">{formData.size}</Typography>
          </div>
          <Divider />
          <div className="flex justify-between w-full items-center">
            <Typography variant="h5">{t("Upload.PrintStatus")}</Typography>
            <Typography variant="h4">
              {formData.status === "1"
                ? t("Upload.Printoneside")
                : t("Upload.Printtwoside")}
            </Typography>
          </div>
          <Divider />
          <div className="flex justify-between w-full items-center">
            <Typography variant="h5">{t("Upload.Color")}</Typography>
            <Typography variant="h4">
              {formData.type === "1" ? t("Upload.BW") : t("Upload.Color")}
            </Typography>
          </div>
          <Divider />
          <div className="flex justify-between w-full items-center">
            <Typography variant="h5">{t("Upload.Scale")}</Typography>
            <Typography variant="h4">
              {formData.isFitPage === false
                ? t("Upload.Actual")
                : t("Upload.Fit")}
            </Typography>
          </div>
          <Divider />
          <div className="flex justify-between w-full items-center">
            <Typography variant="h5"> {t("Upload.Layout")} </Typography>
            <Typography variant="h4">
              {formData.orientartion === "1"
                ? t("Upload.Landscape")
                : t("Upload.Portrait")}
            </Typography>
          </div>
          <Divider />
          <div className="flex justify-between w-full items-center">
            <Typography variant="h5"> {t("Upload.Access")}</Typography>
            <Typography variant="h4">
              {formData.time} {t("Upload.Hour")}{" "}
            </Typography>
          </div>
        </Box>

        <StepNavigation
          onBack={() => dispatch(prevStep())}
          onContinue={handleSubmit(onSubmit)}
          backLabel={t("Upload.Back")}
          continueLabel={loading ? t("Upload.Confirm") : t("Upload.Confirm")}
          showBack={true}
          showContinue={true}
          continueDisabled={loading}
          continueColor="secondary"
          continueIcon={<CheckIcon />}
        />
      </Box>
    </>
  );
}

export default Confirm;
